import { useIndicatorsAndOverlays } from '@/composables/useIndicatorsAndOverlays';
import { useChartDimensions } from '@/composables/useChartDimensions';
import { usePerfChartSymbols } from '@/composables/usePerfChartSymbols';
import { isValidRangeExtraBars } from '@/composables/isValidRangeExtraBars';
import { isSharpCharts2DateStringOlderThanMinimumAllowed } from '@/composables/isSharpCharts2DateStringOlderThanMinimumAllowed';
import { getDefaultSharpCharts2RangeStartDate } from '@/utils/getDefaultSharpCharts2RangeStartDate';
import { getFormattedTodayDate } from '@/utils/getFormattedTodayDate';
import { isValidSharpCharts2DateString } from '@/utils/isValidSharpCharts2DateString';
import { getFormattedYearToDateStartDate } from '@/utils/getFormattedYearToDateStartDate';
import { isValidHexCode } from '@/utils/isValidHexCode';
import { isValidNumber } from '@/utils/isValidNumber';
import { ColorScheme, COLOR_SCHEMES_ARRAY, COLOR_SCHEME_CUSTOM } from '@/enums/color-schemes';
import { ChartSizeOption, CHART_SIZE_MENU_OPTION_CUSTOM, CHART_SIZE_MENU_OPTIONS_ARRAY } from '@/enums/chart-size-menu-options';
import { CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_BACKGROUND, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_GRID, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_TEXT, CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_INDICATOR } from '@/enums/custom-color-scheme-default-colors';
import { UpDownColorOption, UP_DOWN_COLOR_OPTION_AUTO } from '@/enums/up-down-color-options';
import { CHART_GRID_OPTIONS_ARRAY, CHART_GRID_OPTION_NORMAL, CHART_GRID_OPTION_NORMAL_DARK } from '@/enums/chart-grid-options';
import { PERIOD_OPTIONS_ARRAY } from '@/enums/period-options';
import { RANGE_OPTION_FILL_THE_CHART, RANGE_OPTION_PREDEFINED_RANGE, RANGE_OPTION_YEAR_TO_DATE, RANGE_OPTION_SELECT_START_END, RANGE_OPTIONS_ARRAY } from '@/enums/range-options';
import { RANGE_BAR_OPTIONS_ARRAY } from '@/enums/range-bar-options';
import { RANGE_GAP_OPTIONS_ARRAY } from '@/enums/range-gap-options';
import { RangeDaysOption, RANGE_DAYS_OPTIONS_ARRAY } from '@/enums/range-days-options';
import { RangeMonthsOption, RANGE_MONTHS_OPTIONS_ARRAY } from '@/enums/range-months-options';
import { RangeYearsOption, RANGE_YEARS_OPTIONS_ARRAY } from '@/enums/range-years-options';
import { LEGEND_OPTION_OFF, LEGEND_OPTION_MINIMAL, LEGEND_OPTION_DEFAULT, LEGEND_OPTION_VERBOSE } from '@/enums/legend-options';
import { DEFAULT_KAGI_REVERSAL_UNIT, KAGI_REVERSAL_UNITS_ARRAY } from '@/enums/kagi-reversal-unit-options';
import { DEFAULT_KAGI_PRICE_FIELD_OPTION, KAGI_PRICE_FIELD_OPTIONS_ARRAY } from '@/enums/kagi-price-field-options';
import { DEFAULT_RENKO_BOX_SIZE_UNIT_OPTION, RENKO_BOX_SIZE_UNIT_OPTIONS_ARRAY } from '@/enums/renko-box-size-unit-options';
import { DEFAULT_RENKO_PRICE_FIELD_OPTION, RENKO_PRICE_FIELD_OPTIONS_ARRAY } from '@/enums/renko-price-field-options';
import { NONE, PRICE_LABELS, VOLUME, VOLUME_BY_PRICE, ICHIMOKU_CLOUD, ICHIMOKU_CLOUD_FULL, DATE_TIME_AXIS, PRICE, PRICE_PERFORMANCE, PRICE_SAME_SCALE, STANDARD_OVERLAYS_ARRAY, STANDARD_INDICATORS_ARRAY, OVERLAYS_OF_INDICATORS_ARRAY } from '@/enums/indicators-and-overlays';
import { ChartTypeValue, CHART_TYPE_OPTION_PERF_CHART, DEFAULT_CHART_TYPE_OPTION, CHART_TYPE_OPTIONS_ARRAY } from '@/enums/chart-type-options';
import { OPACITY_OPTION_AUTO, OPACITY_OPTION_ZERO_POINT_FIVE, DEFAULT_OPACITY_OPTION, OPACITY_OPTIONS_ARRAY } from '@/enums/opacity-options';
import { CHART_SETTINGS_VOLUME_MENU_OPTION_OVERLAY, CHART_SETTINGS_VOLUME_MENU_OPTION_SEPARATE, CHART_SETTINGS_VOLUME_MENU_OPTION_OFF } from '@/enums/chart-settings-volume-menu-options';
import { OVERLAY_STYLE_AREA, OVERLAY_STYLES_ARRAY } from '@/enums/overlay-styles';
import { INDICATOR_POSITION_ABOVE, INDICATOR_POSITION_BEHIND_PRICE, INDICATOR_POSITION_BELOW, INDICATOR_POSITION_BEHIND_INDICATOR } from '@/enums/indicator-positions';
import { DEFAULT_PRICE_INDICATOR_STYLE_OPTION, PRICE_INDICATOR_STYLE_OPTIONS_ARRAY } from '@/enums/price-indicator-style-options';
import { DEFAULT_INDICATOR_HEIGHT_OPTION, INDICATOR_HEIGHT_OPTIONS_ARRAY } from '@/enums/indicator-height-options';
import { MIN_ALLOWED_DATE_STRING } from '@/enums/min-allowed-date-string';
import { Indicator, Overlay, ChartStyle, PerfChartSymbol } from '@/interfaces/chart';

const ERROR_MESSAGE = 'Failed to parse SharpCharts2 chart XML';

function getAttributeValue(node: Element, attributeName: string, shouldFormat: boolean = true): string {
  const attributeValue = node.attributes.getNamedItem(attributeName)?.value ?? '';

  return shouldFormat ? attributeValue.trim().toLowerCase() : attributeValue;
}

function doesNodeHaveAttribute(node: Element, attributeName: string, allowEmptyString: boolean): boolean {
  const nodeAttribute = node.attributes.getNamedItem(attributeName);

  return (
    !!nodeAttribute &&
    (!!nodeAttribute.value.trim() || allowEmptyString)
  );
}

function doesNodeHaveAttributePair(node: Element, attributeName: string, attributeValue: string): boolean {
  const nodeAttribute = node.attributes.getNamedItem(attributeName);

  return (
    !!nodeAttribute &&
    nodeAttribute.value.trim().toLowerCase() === attributeValue.toLowerCase()
  );
}

function getPropertyNode(propertyNodes: Element[], nodeName: string, allowEmptyString: boolean): Element {
  const formattedNodeName = nodeName.toLowerCase();
  const desiredPropertyNode = propertyNodes.find(propertyNode => getAttributeValue(propertyNode, 'name') === formattedNodeName) ?? null;

  if (
    desiredPropertyNode &&
    !allowEmptyString &&
    !doesNodeHaveAttribute(desiredPropertyNode, 'value', false)
  ) {
    throw new Error(`${ERROR_MESSAGE} - ${formattedNodeName} property node value is invalid.`);
  }

  if (!desiredPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - no ${formattedNodeName} property node found.`);
  }

  return desiredPropertyNode;
}

function getPropertyNodeOrNull(propertyNodes: Element[], nodeName: string, allowEmptyString: boolean): Element | null {
  const formattedNodeName = nodeName.toLowerCase();
  const desiredPropertyNode = propertyNodes.find(propertyNode => getAttributeValue(propertyNode, 'name') === formattedNodeName) ?? null;

  if (
    desiredPropertyNode &&
    !allowEmptyString &&
    !doesNodeHaveAttribute(desiredPropertyNode, 'value', false)
  ) {
    throw new Error(`${ERROR_MESSAGE} - ${formattedNodeName} property node value is invalid.`);
  }

  return desiredPropertyNode;
}

function getChildNodes(parentNode: Element, childNodeName: string): Element[] {
  return Array.from(parentNode.children).filter(child =>
    child.nodeName.trim().toLowerCase() === childNodeName.toLowerCase()
  );
}

function parseColorScheme(propertyNodes: Element[]): ColorScheme {
  const colorSchemePropertyNode = getPropertyNode(propertyNodes, 'chart.skin', false);
  const rawColorSchemeValue = getAttributeValue(colorSchemePropertyNode, 'value');
  const colorScheme = COLOR_SCHEMES_ARRAY.find(colorScheme => colorScheme.sharpCharts2Id.toLowerCase() === rawColorSchemeValue);

  if (!colorScheme) {
    throw new Error(`${ERROR_MESSAGE} - no matching color scheme for ${rawColorSchemeValue} found.`);
  }

  return colorScheme;
}

function parseChartSize(propertyNodes: Element[]): ChartSizeOption {
  let chartSizePropertyNode = getPropertyNodeOrNull(propertyNodes, 'ui.chart.size', false);

  if (!chartSizePropertyNode) {
    chartSizePropertyNode = getPropertyNode(propertyNodes, 'chart.width', false);
  }

  const rawChartSizeValue = getAttributeValue(chartSizePropertyNode, 'value');
  const chartSize = CHART_SIZE_MENU_OPTIONS_ARRAY.find(chartSize => chartSize.sharpCharts2Id.toLowerCase() === rawChartSizeValue);

  if (!chartSize) {
    throw new Error(`${ERROR_MESSAGE} - no matching chart size for ${rawChartSizeValue} found.`);
  }

  return chartSize;
}

function getCustomChartWidthAndCustomChartHeight(chartSize: ChartSizeOption, propertyNodes: Element[]): { customWidth: number, customHeight: number } {
  const { isValidCustomWidth, isValidCustomHeight } = useChartDimensions();

  if (chartSize !== CHART_SIZE_MENU_OPTION_CUSTOM) {
    return {
      customWidth: CHART_SIZE_MENU_OPTION_CUSTOM.width,
      customHeight: CHART_SIZE_MENU_OPTION_CUSTOM.height
    };
  }

  const chartWidthPropertyNode = getPropertyNode(propertyNodes, 'chart.width', false);
  const rawChartWidthValue = getAttributeValue(chartWidthPropertyNode, 'value');

  if (!isValidCustomWidth(rawChartWidthValue)) {
    throw new Error(`${ERROR_MESSAGE} - chart.width property value is invalid: ${rawChartWidthValue}.`);
  }

  const chartHeightPropertyNode = getPropertyNode(propertyNodes, 'chart.height', false);
  const rawChartHeightValue = getAttributeValue(chartHeightPropertyNode, 'value');

  if (!isValidCustomHeight(rawChartHeightValue)) {
    throw new Error(`${ERROR_MESSAGE} - chart.height property value is invalid: ${rawChartHeightValue}.`);
  }

  return {
    customWidth: Number(rawChartWidthValue),
    customHeight: Number(rawChartHeightValue)
  };
}

function getCustomColorSchemeColors(colorScheme: ColorScheme, propertyNodes: Element[]): { customColorSchemeBackgroundColor: string, customColorSchemeGridColor: string, customColorSchemeTextColor: string, customColorSchemeIndicatorColor: string } {
  if (colorScheme !== COLOR_SCHEME_CUSTOM) {
    return {
      customColorSchemeBackgroundColor: CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_BACKGROUND,
      customColorSchemeGridColor: CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_GRID,
      customColorSchemeTextColor: CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_TEXT,
      customColorSchemeIndicatorColor: CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_INDICATOR
    };
  }

  const customColorBackgroundPropertyNode = getPropertyNode(propertyNodes, 'custom.color.background', true);
  let rawCustomColorBackground = getAttributeValue(customColorBackgroundPropertyNode, 'value').toUpperCase() || CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_BACKGROUND;

  if (!rawCustomColorBackground.startsWith('#')) {
    rawCustomColorBackground = `#${rawCustomColorBackground}`;
  }

  if (!isValidHexCode(rawCustomColorBackground)) {
    throw new Error(`${ERROR_MESSAGE} - custom.color.background property value is not a valid hex code: ${rawCustomColorBackground}.`);
  }

  const customColorGridPropertyNode = getPropertyNode(propertyNodes, 'custom.color.grid', true);
  let rawCustomColorGrid = getAttributeValue(customColorGridPropertyNode, 'value').toUpperCase() || CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_GRID;

  if (!rawCustomColorGrid.startsWith('#')) {
    rawCustomColorGrid = `#${rawCustomColorGrid}`;
  }

  if (!isValidHexCode(rawCustomColorGrid)) {
    throw new Error(`${ERROR_MESSAGE} - custom.color.grid property value is not a valid hex code: ${rawCustomColorGrid}.`);
  }

  const customColorTextPropertyNode = getPropertyNode(propertyNodes, 'custom.color.text', true);
  let rawCustomColorText = getAttributeValue(customColorTextPropertyNode, 'value').toUpperCase() || CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_TEXT;

  if (!rawCustomColorText.startsWith('#')) {
    rawCustomColorText = `#${rawCustomColorText}`;
  }

  if (!isValidHexCode(rawCustomColorText)) {
    throw new Error(`${ERROR_MESSAGE} - custom.color.text property value is not a valid hex code: ${rawCustomColorText}.`);
  }

  const customColorIndicatorPropertyNode = getPropertyNode(propertyNodes, 'custom.color.indicator', true);
  let rawCustomColorIndicator = getAttributeValue(customColorIndicatorPropertyNode, 'value').toUpperCase() || CUSTOM_COLOR_SCHEME_DEFAULT_COLOR_INDICATOR;

  if (!rawCustomColorIndicator.startsWith('#')) {
    rawCustomColorIndicator = `#${rawCustomColorIndicator}`;
  }

  if (!isValidHexCode(rawCustomColorIndicator)) {
    throw new Error(`${ERROR_MESSAGE} - custom.color.indicator property value is not a valid hex code: ${rawCustomColorIndicator}.`);
  }

  return {
    customColorSchemeBackgroundColor: rawCustomColorBackground,
    customColorSchemeGridColor: rawCustomColorGrid,
    customColorSchemeTextColor: rawCustomColorText,
    customColorSchemeIndicatorColor: rawCustomColorIndicator
  };
}

function getUpDownColorsFromPropertyNodes(colorScheme: ColorScheme, propertyNodes: Element[]): { upColor: UpDownColorOption, downColor: UpDownColorOption } {
  const paletteUpVolPropertyNode = getPropertyNodeOrNull(propertyNodes, 'palette.upVol', false);
  const paletteDownVolPropertyNode = getPropertyNodeOrNull(propertyNodes, 'palette.downVol', false);

  if (!paletteUpVolPropertyNode && paletteDownVolPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - palette.downVol property is present but no palette.upVol property was found.`);
  }

  if (paletteUpVolPropertyNode && !paletteDownVolPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - palette.upVol property is present but no palette.downVol property was found.`);
  }

  let rawPaletteUpVol = '';
  let rawPaletteDownVol = '';

  if (paletteUpVolPropertyNode && paletteDownVolPropertyNode) {
    rawPaletteUpVol = getAttributeValue(paletteUpVolPropertyNode, 'value').toUpperCase();

    if (!rawPaletteUpVol.startsWith('#')) {
      rawPaletteUpVol = `#${rawPaletteUpVol}`;
    }

    if (!isValidHexCode(rawPaletteUpVol)) {
      throw new Error(`${ERROR_MESSAGE} - palette.upVol property value is not a valid hex code: ${rawPaletteUpVol}.`);
    }

    rawPaletteUpVol = rawPaletteUpVol.slice(1);
    rawPaletteDownVol = getAttributeValue(paletteDownVolPropertyNode, 'value').toUpperCase();

    if (!rawPaletteDownVol.startsWith('#')) {
      rawPaletteDownVol = `#${rawPaletteDownVol}`;
    }

    if (!isValidHexCode(rawPaletteDownVol)) {
      throw new Error(`${ERROR_MESSAGE} - palette.downVol property value is not a valid hex code: ${rawPaletteDownVol}.`);
    }

    rawPaletteDownVol = rawPaletteDownVol.slice(1);
  }

  const paletteUpPricePropertyNode = getPropertyNodeOrNull(propertyNodes, 'palette.upPrice', false);
  const paletteDownPricePropertyNode = getPropertyNodeOrNull(propertyNodes, 'palette.downPrice', false);

  if (!paletteUpPricePropertyNode && paletteDownPricePropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - palette.downPrice property is present but no palette.upPrice property was found.`);
  }

  if (paletteUpPricePropertyNode && !paletteDownPricePropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - palette.upPrice property is present but no palette.downPrice property was found.`);
  }

  if (
    (!paletteUpPricePropertyNode && !paletteDownPricePropertyNode) &&
    (paletteUpVolPropertyNode && paletteDownVolPropertyNode)
  ) {
    throw new Error(`${ERROR_MESSAGE} - palette.upVol and palette.downVol properties are present but palette.upPrice and palette.downPrice properties were not found.`);
  }

  let upColor = UP_DOWN_COLOR_OPTION_AUTO;
  let downColor = UP_DOWN_COLOR_OPTION_AUTO;

  if (!paletteUpPricePropertyNode || !paletteDownPricePropertyNode) {
    return { upColor, downColor };
  }

  let rawPaletteUpPrice = getAttributeValue(paletteUpPricePropertyNode, 'value').toUpperCase();

  if (!rawPaletteUpPrice.startsWith('#')) {
    rawPaletteUpPrice = `#${rawPaletteUpPrice}`;
  }

  if (!isValidHexCode(rawPaletteUpPrice)) {
    throw new Error(`${ERROR_MESSAGE} - palette.upPrice property value is not a valid hex code: ${rawPaletteUpPrice}.`);
  }

  rawPaletteUpPrice = rawPaletteUpPrice.slice(1);
  let rawPaletteDownPrice = getAttributeValue(paletteDownPricePropertyNode, 'value').toUpperCase();

  if (!rawPaletteDownPrice.startsWith('#')) {
    rawPaletteDownPrice = `#${rawPaletteDownPrice}`;
  }

  if (!isValidHexCode(rawPaletteDownPrice)) {
    throw new Error(`${ERROR_MESSAGE} - palette.downPrice property value is not a valid hex code: ${rawPaletteDownPrice}.`);
  }

  rawPaletteDownPrice = rawPaletteDownPrice.slice(1);

  if (rawPaletteUpPrice === rawPaletteDownPrice) {
    throw new Error(`${ERROR_MESSAGE} - palette.upPrice property value: ${rawPaletteUpPrice} is equal to palette.downPrice property value: ${rawPaletteDownPrice}.`);
  }

  if (paletteUpVolPropertyNode && paletteDownVolPropertyNode) {
    if (rawPaletteUpPrice !== rawPaletteUpVol) {
      throw new Error(`${ERROR_MESSAGE} - palette.upPrice property value: ${rawPaletteUpPrice} does not match palette.upVol property value: ${rawPaletteUpVol}.`);
    }

    if (rawPaletteDownPrice !== rawPaletteDownVol) {
      throw new Error(`${ERROR_MESSAGE} - palette.downPrice property value: ${rawPaletteDownPrice} does not match palette.downVol property value: ${rawPaletteDownVol}.`);
    }
  }

  if (rawPaletteUpPrice !== colorScheme.defaultSharpCharts2OverlayLineSwatchUpColor.toUpperCase()) {
    const upColorSwatch = colorScheme.sharpCharts2OverlaySwatches.find(swatch => swatch.lineColor.toUpperCase() === rawPaletteUpPrice);

    if (!upColorSwatch) {
      throw new Error(`${ERROR_MESSAGE} - palette.upPrice property value: ${rawPaletteUpPrice} does not match any swatch colors of color scheme: ${colorScheme.sharpCharts2Id}.`);
    }

    upColor = upColorSwatch.upDownColorOption;
  }

  if (rawPaletteDownPrice !== colorScheme.defaultSharpCharts2OverlayLineSwatchDownColor.toUpperCase()) {
    const downColorSwatch = colorScheme.sharpCharts2OverlaySwatches.find(swatch => swatch.lineColor.toUpperCase() === rawPaletteDownPrice);

    if (!downColorSwatch) {
      throw new Error(`${ERROR_MESSAGE} - palette.downPrice property value: ${rawPaletteDownPrice} does not match any swatch colors of color scheme: ${colorScheme.sharpCharts2Id}.`);
    }

    downColor = downColorSwatch.upDownColorOption;
  }

  return { upColor, downColor };
}

function getChartGrid(propertyNodes: Element[]): { chartGrid: string } {
  const gridDensityHorizPropertyNode = getPropertyNodeOrNull(propertyNodes, 'grid.density.horiz', true);
  const gridAlphaLinesMajor1PropertyNode = getPropertyNodeOrNull(propertyNodes, 'grid.alpha.lines.major.1', true);
  const isDark = !!gridAlphaLinesMajor1PropertyNode;

  let chartGrid = isDark ? CHART_GRID_OPTION_NORMAL_DARK.title : CHART_GRID_OPTION_NORMAL.title;

  if (!gridDensityHorizPropertyNode || !doesNodeHaveAttribute(gridDensityHorizPropertyNode, 'value', true)) {
    return { chartGrid };
  }

  const rawGridDensityHorizValue = getAttributeValue(gridDensityHorizPropertyNode, 'value');
  const chartGridFromGridDensityHorizValue = CHART_GRID_OPTIONS_ARRAY.find(chartGridOption =>
    chartGridOption.sharpCharts2Id.toLowerCase() === rawGridDensityHorizValue &&
    chartGridOption.isDark === isDark
  );

  if (chartGridFromGridDensityHorizValue) {
    chartGrid = chartGridFromGridDensityHorizValue.title;
  }

  return { chartGrid };
}

function getPeriod(propertyNodes: Element[]): { period: string } {
  const dataTypePropertyNode = getPropertyNode(propertyNodes, 'data.type', false);
  const rawDataTypeValue = getAttributeValue(dataTypePropertyNode, 'value');
  const periodOption = PERIOD_OPTIONS_ARRAY.find(p => p.sharpCharts2Id.toLowerCase() === rawDataTypeValue);

  if (!periodOption) {
    throw new Error(`${ERROR_MESSAGE} - no matching period for data.type value: ${rawDataTypeValue} found.`);
  }

  return { period: periodOption.value };
}

function getRange(propertyNodes: Element[]): { range: string, rangeBar: string, rangeGap: string, rangeDays: RangeDaysOption, rangeMonths: RangeMonthsOption, rangeYears: RangeYearsOption, rangeStartDate: string, rangeEndDate: string } {
  const dataBarWidthPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.barWidth', false);
  const dataGapWidthPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.gapWidth', false);
  const dataDaysPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.days', false);
  const dataStartPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.start', true);
  const dataEndPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.end', true);

  if (dataBarWidthPropertyNode && !dataGapWidthPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - data.barWidth property node is present but data.gapWidth property node was not found`);
  }

  if (!dataBarWidthPropertyNode && dataGapWidthPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - data.gapWidth property node is present but data.barWidth property node was not found`);
  }

  if (dataStartPropertyNode && !dataEndPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - data.start property node is present but data.end property node was not found`);
  }

  if (!dataStartPropertyNode && dataEndPropertyNode) {
    throw new Error(`${ERROR_MESSAGE} - data.end property node is present but data.start property node was not found`);
  }

  const useBarAndGap = !!dataBarWidthPropertyNode && !!dataGapWidthPropertyNode;
  const useNumberOfDays = !!dataDaysPropertyNode;
  const useStartDateAndEndDate = !!dataStartPropertyNode && !!dataEndPropertyNode;

  if (useBarAndGap && useNumberOfDays && useStartDateAndEndDate) {
    throw new Error(`${ERROR_MESSAGE} - data.barWidth, data.gapWidth, data.days, data.start, and data.end property nodes are all present.`);
  }

  if (!useBarAndGap && !useNumberOfDays && !useStartDateAndEndDate) {
    throw new Error(`${ERROR_MESSAGE} - data.barWidth, data.gapWidth, data.days, data.start, and data.end property nodes are all absent.`);
  }

  if (useBarAndGap && !useNumberOfDays && useStartDateAndEndDate) {
    throw new Error(`${ERROR_MESSAGE} - data.barWidth, data.gapWidth, data.start, and data.end property nodes are all present.`);
  }

  if (useBarAndGap && useNumberOfDays && !useStartDateAndEndDate) {
    throw new Error(`${ERROR_MESSAGE} - data.barWidth, data.gapWidth, and data.days property nodes are all present.`);
  }

  if (!useBarAndGap && useNumberOfDays && useStartDateAndEndDate) {
    throw new Error(`${ERROR_MESSAGE} - data.days, data.start, and data.end property nodes are all present.`);
  }

  let rangeStartDate = getDefaultSharpCharts2RangeStartDate();
  let rangeEndDate = '';

  if (useBarAndGap) {
    const rawBarWidthValue = getAttributeValue(dataBarWidthPropertyNode, 'value');
    const rangeBar = RANGE_BAR_OPTIONS_ARRAY.find(rangeBarOption => rangeBarOption.toLowerCase() === rawBarWidthValue);

    if (!rangeBar) {
      throw new Error(`${ERROR_MESSAGE} - no matching range bar width for data.barWidth value: ${rawBarWidthValue} found.`);
    }

    const rawGapWidthValue = getAttributeValue(dataGapWidthPropertyNode, 'value');
    const rangeGap = RANGE_GAP_OPTIONS_ARRAY.find(rangeGapOption => rangeGapOption.toLowerCase() === rawGapWidthValue);

    if (!rangeGap) {
      throw new Error(`${ERROR_MESSAGE} - no matching range gap width for data.gapWidth value: ${rawGapWidthValue} found.`);
    }

    return {
      rangeStartDate,
      rangeEndDate,
      range: RANGE_OPTION_FILL_THE_CHART.title,
      rangeBar,
      rangeGap,
      rangeDays: RANGE_OPTION_FILL_THE_CHART.numberOfDays,
      rangeMonths: RANGE_OPTION_FILL_THE_CHART.numberOfMonths,
      rangeYears: RANGE_OPTION_FILL_THE_CHART.numberOfYears
    };
  }

  if (useStartDateAndEndDate) {
    let startDate = '';

    if (doesNodeHaveAttribute(dataStartPropertyNode, 'value', true)) {
      startDate = getAttributeValue(dataStartPropertyNode, 'value');
    }

    if (startDate.toLowerCase().includes('today')) {
      startDate = '';
    }

    if (!isValidSharpCharts2DateString(startDate)) {
      throw new Error(`${ERROR_MESSAGE} - data.start property node value has an invalid date format: ${startDate}.`);
    }

    if (isSharpCharts2DateStringOlderThanMinimumAllowed(startDate)) {
      startDate = MIN_ALLOWED_DATE_STRING;
    }

    let endDate = '';

    if (doesNodeHaveAttribute(dataEndPropertyNode, 'value', true)) {
      endDate = getAttributeValue(dataEndPropertyNode, 'value');
    }

    if (endDate.toLowerCase().includes('today')) {
      endDate = '';
    }

    if (!isValidSharpCharts2DateString(endDate)) {
      throw new Error(`${ERROR_MESSAGE} - data.end property node value has an invalid date format: ${endDate}.`);
    }

    if (isSharpCharts2DateStringOlderThanMinimumAllowed(endDate)) {
      endDate = MIN_ALLOWED_DATE_STRING;
    }

    const today = getFormattedTodayDate();

    if (startDate === today) {
      startDate = '';
    }

    if (endDate === today) {
      endDate = '';
    }

    const rangeOption = startDate === getFormattedYearToDateStartDate() && endDate === '' ? RANGE_OPTION_YEAR_TO_DATE : RANGE_OPTION_SELECT_START_END;

    return {
      rangeStartDate: startDate,
      rangeEndDate: endDate,
      range: rangeOption.title,
      rangeBar: rangeOption.barValue,
      rangeGap: rangeOption.gapValue,
      rangeDays: rangeOption.numberOfDays,
      rangeMonths: rangeOption.numberOfMonths,
      rangeYears: rangeOption.numberOfYears
    };
  }

  if (!useNumberOfDays) {
    throw new Error(`${ERROR_MESSAGE} - unable to determine chart range.`);
  }

  const rawDataDaysValue = getAttributeValue(dataDaysPropertyNode, 'value');

  if (!isValidNumber(rawDataDaysValue, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanZero: true })) {
    throw new Error(`${ERROR_MESSAGE} - data.days property value is not a valid number: ${rawDataDaysValue}.`);
  }

  let dataDays = Number(rawDataDaysValue);
  const numYears = Math.floor(dataDays / 365);
  const numYearsString = `${numYears}`;
  dataDays -= 365 * numYears;
  let numMonths = Math.floor(dataDays / 30);
  let numMonthsString = `${numMonths}`;
  dataDays -= 30 * numMonths;
  const rangeYears = RANGE_YEARS_OPTIONS_ARRAY.find(rangeYearsOption => rangeYearsOption === numYearsString);

  if (!rangeYears) {
    throw new Error(`${ERROR_MESSAGE} - parsed number of years: ${numYearsString} from data.days property value: ${rawDataDaysValue} does not match any range years options.`);
  }

  let rangeMonths = RANGE_MONTHS_OPTIONS_ARRAY.find(rangeMonthsOption => rangeMonthsOption === numMonthsString);

  if (!rangeMonths) {
    numMonths--;
    numMonthsString = `${numMonths}`;
    dataDays += 30;
  }

  rangeMonths = RANGE_MONTHS_OPTIONS_ARRAY.find(rangeMonthsOption => rangeMonthsOption === numMonthsString);

  if (!rangeMonths) {
    throw new Error(`${ERROR_MESSAGE} - parsed number of months: ${numMonthsString} from data.days property value: ${rawDataDaysValue} does not match any range months options.`);
  }

  const numDays = dataDays;
  const numDaysString = `${numDays}`;

  const rangeDays = RANGE_DAYS_OPTIONS_ARRAY.find(rangeDaysOption => rangeDaysOption === numDaysString);

  if (!rangeDays) {
    throw new Error(`${ERROR_MESSAGE} - parsed number of days: ${numDaysString} from data.days property value: ${rawDataDaysValue} does not match any range days options.`);
  }

  const rangeOption = RANGE_OPTIONS_ARRAY.find(range =>
    range.useNumberOfDays &&
    range !== RANGE_OPTION_PREDEFINED_RANGE &&
    range.numberOfDays === rangeDays &&
    range.numberOfMonths === rangeMonths &&
    range.numberOfYears === rangeYears
  ) ?? RANGE_OPTION_PREDEFINED_RANGE;

  return {
    rangeStartDate,
    rangeEndDate,
    range: rangeOption.title,
    rangeBar: rangeOption.barValue,
    rangeGap: rangeOption.gapValue,
    rangeDays,
    rangeMonths,
    rangeYears
  };
}

function getExtraBars(propertyNodes: Element[]): { rangeExtraBars: number } {
  const dataExtraPeriodsPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.extraperiods', true);

  if (!dataExtraPeriodsPropertyNode || !doesNodeHaveAttribute(dataExtraPeriodsPropertyNode, 'value', true)) {
    return { rangeExtraBars: 0 };
  }

  const rawDataExtraPeriodsValue = getAttributeValue(dataExtraPeriodsPropertyNode, 'value');

  if (!isValidRangeExtraBars(rawDataExtraPeriodsValue)) {
    throw new Error(`${ERROR_MESSAGE} - data.extraperiods property node value is invalid: ${rawDataExtraPeriodsValue}.`);
  }

  return { rangeExtraBars: rawDataExtraPeriodsValue === '' ? 0 : Number(rawDataExtraPeriodsValue) };
}

function getLegends(propertyNodes: Element[]): { legends: string } {
  const gridShowCompanyNamesPropertyNode = getPropertyNodeOrNull(propertyNodes, 'grid.show.companynames', true);

  if (
    gridShowCompanyNamesPropertyNode &&
    doesNodeHaveAttributePair(gridShowCompanyNamesPropertyNode, 'value', 'true')
  ) {
    return { legends: LEGEND_OPTION_VERBOSE.title };
  }

  const gridShowLayerTitlesPropertyNode = getPropertyNodeOrNull(propertyNodes, 'grid.show.layerTitles', true);
  const gridShowOverlayTitlesPropertyNode = getPropertyNodeOrNull(propertyNodes, 'grid.show.overlayTitles', true);

  if (
    gridShowLayerTitlesPropertyNode &&
    gridShowOverlayTitlesPropertyNode &&
    doesNodeHaveAttributePair(gridShowLayerTitlesPropertyNode, 'value', 'price') &&
    doesNodeHaveAttributePair(gridShowOverlayTitlesPropertyNode, 'value', 'false')
  ) {
    return { legends: LEGEND_OPTION_MINIMAL.title };
  }

  if (
    gridShowLayerTitlesPropertyNode &&
    gridShowOverlayTitlesPropertyNode &&
    doesNodeHaveAttributePair(gridShowLayerTitlesPropertyNode, 'value', 'false') &&
    doesNodeHaveAttributePair(gridShowOverlayTitlesPropertyNode, 'value', 'false')
  ) {
    return { legends: LEGEND_OPTION_OFF.title };
  }

  return { legends: LEGEND_OPTION_DEFAULT.title };
}

function getYAxisLabels(propertyNodes: Element[]): { yAxisLabels: boolean } {
  const gridShowStreamingLabelsPropertyNode = getPropertyNode(propertyNodes, 'grid.show.streaminglabels', false);
  const rawGridShowStreamingLabelsValue = getAttributeValue(gridShowStreamingLabelsPropertyNode, 'value');

  if (!['true', 'false'].includes(rawGridShowStreamingLabelsValue)) {
    throw new Error(`${ERROR_MESSAGE} - grid.show.streaminglabels property value is invalid: ${rawGridShowStreamingLabelsValue}.`);
  }

  return { yAxisLabels: rawGridShowStreamingLabelsValue === 'true' };
}

function getZoomThumbnail(propertyNodes: Element[]): { zoomThumbnail: boolean } {
  const gridShowZoomThumbnailPropertyNode = getPropertyNodeOrNull(propertyNodes, 'chart.show.zoomthumbnail', true);

  if (!gridShowZoomThumbnailPropertyNode || !doesNodeHaveAttribute(gridShowZoomThumbnailPropertyNode, 'value', false)) {
    return { zoomThumbnail: false };
  }

  const rawGridShowZoomThumbnailValue = getAttributeValue(gridShowZoomThumbnailPropertyNode, 'value');

  if (!['true', 'false'].includes(rawGridShowZoomThumbnailValue)) {
    throw new Error(`${ERROR_MESSAGE} - chart.show.zoomthumbnail property value is invalid: ${rawGridShowZoomThumbnailValue}.`);
  }

  return { zoomThumbnail: rawGridShowZoomThumbnailValue === 'true' };
}

function getExtendedHours(propertyNodes: Element[]): { extendedHours: boolean } {
  const gridShowExtendedHoursPropertyNode = getPropertyNodeOrNull(propertyNodes, 'chart.show.extendedhours', true);

  if (!gridShowExtendedHoursPropertyNode || !doesNodeHaveAttribute(gridShowExtendedHoursPropertyNode, 'value', false)) {
    return { extendedHours: false };
  }

  const rawGridShowExtendedHoursValue = getAttributeValue(gridShowExtendedHoursPropertyNode, 'value');

  if (!['true', 'false'].includes(rawGridShowExtendedHoursValue)) {
    throw new Error(`${ERROR_MESSAGE} - chart.show.extendedhours property value is invalid: ${rawGridShowExtendedHoursValue}.`);
  }

  return { extendedHours: rawGridShowExtendedHoursValue === 'true' };
}

function getUseMainChartSymbolAsBaseline(propertyNodes: Element[]): { useMainChartSymbolAsBaseline: boolean } {
  const dataCompareModePropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.compare.mode', true);

  if (!dataCompareModePropertyNode || !doesNodeHaveAttribute(dataCompareModePropertyNode, 'value', false)) {
    return { useMainChartSymbolAsBaseline: false };
  }

  const rawDataCompareModeValue = getAttributeValue(dataCompareModePropertyNode, 'value');

  if (!['pct', 'perf'].includes(rawDataCompareModeValue)) {
    throw new Error(`${ERROR_MESSAGE} - data.compare.mode property value is invalid: ${rawDataCompareModeValue}.`);
  }

  return { useMainChartSymbolAsBaseline: rawDataCompareModeValue === 'perf' };
}

function getPerfChartSymbolsObjects(propertyNodes: Element[]): { perfChartSymbols: PerfChartSymbol[] } {
  const { getPerfChartSymbols } = usePerfChartSymbols();

  const dataCompareSymbolsPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.compare.symbols', true);

  if (!dataCompareSymbolsPropertyNode || !doesNodeHaveAttribute(dataCompareSymbolsPropertyNode, 'value', true)) {
    return { perfChartSymbols: [] };
  }

  const rawDataCompareSymbolsValue = getAttributeValue(dataCompareSymbolsPropertyNode, 'value');

  return { perfChartSymbols: getPerfChartSymbols(rawDataCompareSymbolsValue) };
}

function getKagiRelatedProperties(masterDatasetNode: Element): { kagiReversal: string, kagiUnit: string, kagiPriceField: string } {
  let kagiReversal = DEFAULT_KAGI_REVERSAL_UNIT.defaultKagiReversalValue;
  let kagiUnit = DEFAULT_KAGI_REVERSAL_UNIT.sharpCharts2Id;
  let kagiPriceField = DEFAULT_KAGI_PRICE_FIELD_OPTION.sharpCharts2Id;

  if (!doesNodeHaveAttribute(masterDatasetNode, 'filter', false)) {
    return { kagiReversal, kagiUnit, kagiPriceField };
  }

  const rawMasterDatasetNodeFilterValue = getAttributeValue(masterDatasetNode, 'filter');
  const rawMasterDatasetNodeFilterValueParts = rawMasterDatasetNodeFilterValue.split(',').map(p => p.trim());

  if (rawMasterDatasetNodeFilterValueParts[0] !== 'kagi') {
    return { kagiReversal, kagiUnit, kagiPriceField };
  }

  if (rawMasterDatasetNodeFilterValueParts.length !== 4) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be kagi-related but does not have 3 distinct valid kagi property values: ${rawMasterDatasetNodeFilterValue}.`);
  }

  kagiReversal = rawMasterDatasetNodeFilterValueParts[1];

  const kagiUnitObject = KAGI_REVERSAL_UNITS_ARRAY.find(kagiUnitOption => kagiUnitOption.sharpCharts2Id.toLowerCase() === rawMasterDatasetNodeFilterValueParts[2]);

  if (!kagiUnitObject) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be kagi-related but does not have a kagi reversal unit that matches any valid options: ${rawMasterDatasetNodeFilterValueParts[2]}.`);
  }

  kagiUnit = kagiUnitObject.sharpCharts2Id;

  const kagiPriceFieldObject = KAGI_PRICE_FIELD_OPTIONS_ARRAY.find(kagiPriceFieldOption => kagiPriceFieldOption.sharpCharts2Id.toLowerCase() === rawMasterDatasetNodeFilterValueParts[3]);

  if (!kagiPriceFieldObject) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be kagi-related but does not have a kagi price field that matches any valid options: ${rawMasterDatasetNodeFilterValueParts[3]}.`);
  }

  kagiPriceField = kagiPriceFieldObject.sharpCharts2Id;

  return { kagiReversal, kagiUnit, kagiPriceField };
}

function getRenkoRelatedProperties(masterDatasetNode: Element): { renkoBoxSize: string, renkoUnit: string, renkoPriceField: string } {
  let renkoBoxSize = DEFAULT_RENKO_BOX_SIZE_UNIT_OPTION.defaultRenkoBoxSize;
  let renkoUnit = DEFAULT_RENKO_BOX_SIZE_UNIT_OPTION.sharpCharts2Id;
  let renkoPriceField = DEFAULT_RENKO_PRICE_FIELD_OPTION.sharpCharts2Id;

  if (!doesNodeHaveAttribute(masterDatasetNode, 'filter', false)) {
    return { renkoBoxSize, renkoUnit, renkoPriceField };
  }

  const rawMasterDatasetNodeFilterValue = getAttributeValue(masterDatasetNode, 'filter');
  const rawMasterDatasetNodeFilterValueParts = rawMasterDatasetNodeFilterValue.split(',').map(p => p.trim());

  if (rawMasterDatasetNodeFilterValueParts[0] !== 'renko') {
    return { renkoBoxSize, renkoUnit, renkoPriceField };
  }

  if (rawMasterDatasetNodeFilterValueParts.length !== 4) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be renko-related but does not have 3 distinct valid renko property values: ${rawMasterDatasetNodeFilterValue}.`);
  }

  renkoBoxSize = rawMasterDatasetNodeFilterValueParts[1];

  const renkoUnitObject = RENKO_BOX_SIZE_UNIT_OPTIONS_ARRAY.find(renkoBoxSizeUnitOption => renkoBoxSizeUnitOption.sharpCharts2Id.toLowerCase() === rawMasterDatasetNodeFilterValueParts[2]);

  if (!renkoUnitObject) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be renko-related but does not have a renko box size unit that matches any valid options: ${rawMasterDatasetNodeFilterValueParts[2]}.`);
  }

  renkoUnit = renkoUnitObject.sharpCharts2Id;

  const renkoPriceFieldObject = RENKO_PRICE_FIELD_OPTIONS_ARRAY.find(renkoPriceFieldOption => renkoPriceFieldOption.sharpCharts2Id.toLowerCase() === rawMasterDatasetNodeFilterValueParts[3]);

  if (!renkoPriceFieldObject) {
    throw new Error(`${ERROR_MESSAGE} - master dataset node filter value appears to be renko-related but does not have a renko price field that matches any valid options: ${rawMasterDatasetNodeFilterValueParts[3]}.`);
  }

  renkoPriceField = renkoPriceFieldObject.sharpCharts2Id;

  return { renkoBoxSize, renkoUnit, renkoPriceField };
}

function getChartStoreSettingsFromPanelNodes(colorScheme: ColorScheme, propertyNodes: Element[], panelNodes: Element[]): { chartType: ChartTypeValue, opacity: string, upColor: UpDownColorOption, downColor: UpDownColorOption, fullQuote: boolean, priceLabels: boolean, isLogScale: boolean, solidCandles: boolean, chartSettingsVolumeMenuValue: string, colorVolume: boolean, sortedIndicators: Indicator[], sortedOverlays: Overlay[] } {
  const {
    MAXIMUM_NUMBER_OF_INDICATORS,
    MAXIMUM_NUMBER_OF_OVERLAYS,
    getBlankIndicator,
    getBlankOverlay,
    formatIndicatorAndOverlayParameters,
    formatPriceIndicatorParameters,
  } = useIndicatorsAndOverlays();

  let chartType = DEFAULT_CHART_TYPE_OPTION.value;
  let opacity = DEFAULT_OPACITY_OPTION.title;
  let { upColor, downColor } = getUpDownColorsFromPropertyNodes(colorScheme, propertyNodes);
  let fullQuote = false;
  let priceLabels = false;
  let isLogScale = false;
  let solidCandles = false;
  let chartSettingsVolumeMenuValue = CHART_SETTINGS_VOLUME_MENU_OPTION_OFF;
  let colorVolume = false;
  const sortedIndicators = [];
  const sortedOverlays = [];
  let hasParsedPricePanel = false;

  for (const panelNode of panelNodes) {
    let isHeaderPanel = false;
    let isFullQuotePanel = false;
    let isPricePanel = false;
    const layerNodes = getChildNodes(panelNode, 'layer');

    for (let i = 0; i < layerNodes.length; i++) {
      const layerNode = layerNodes[i];
      const isFirstLayerInPanel = i === 0;
      const scaleNode = getChildNodes(layerNode, 'scale')[0];
      const datasetNodes = getChildNodes(layerNode, 'dataset');
      let isPriceLayer = false;

      for (const datasetNode of datasetNodes) {
        const styleNodes = getChildNodes(datasetNode, 'style');

        for (const styleNode of styleNodes) {
          if (
            doesNodeHaveAttribute(styleNode, 'signal', false) &&
            (doesNodeHaveAttributePair(datasetNode, 'class', VOLUME.sharpCharts2Id) || doesNodeHaveAttributePair(datasetNode, 'class', VOLUME_BY_PRICE.sharpCharts2Id))
          ) {
            colorVolume = true;
          }
        }

        if (doesNodeHaveAttributePair(datasetNode, 'class', 'HEADER')) {
          isHeaderPanel = true;
          break;
        }

        if (doesNodeHaveAttributePair(datasetNode, 'class', 'BIGQ')) {
          fullQuote = true;
          isFullQuotePanel = true;
          break;
        }

        const isPriceDataset = doesNodeHaveAttributePair(datasetNode, 'id', 'price');
        isPricePanel = isPricePanel || isPriceDataset;
        isPriceLayer = isPriceLayer || isPriceDataset;
        hasParsedPricePanel = hasParsedPricePanel || isPricePanel;

        if (
          doesNodeHaveAttributePair(datasetNode, 'class', PRICE_LABELS.sharpCharts2Id) &&
          (doesNodeHaveAttributePair(datasetNode, 'parent', 'price') || doesNodeHaveAttributePair(datasetNode, 'parent', 'master'))
        ) {
          priceLabels = true;
          continue;
        }

        if (doesNodeHaveAttributePair(datasetNode, 'id', 'volUnder')) {
          chartSettingsVolumeMenuValue = CHART_SETTINGS_VOLUME_MENU_OPTION_OVERLAY;
          break;
        }

        if (doesNodeHaveAttributePair(datasetNode, 'id', 'volBelow')) {
          chartSettingsVolumeMenuValue = CHART_SETTINGS_VOLUME_MENU_OPTION_SEPARATE;
          break;
        }

        const isOverlay = isPriceLayer && !isPriceDataset;
        const isOverlayOfIndicator =
          doesNodeHaveAttribute(datasetNode, 'parent', false) &&
          !doesNodeHaveAttributePair(datasetNode, 'parent', 'price') &&
          !doesNodeHaveAttributePair(datasetNode, 'parent', 'master');
        const isIndicatorPositionedBehindPrice = isPricePanel && !isPriceLayer && !isOverlayOfIndicator;
        const isIndicatorPositionedAboveOrBelowPrice = !isPricePanel && isFirstLayerInPanel && !isOverlayOfIndicator;
        const isIndicatorPositionedBehindAnotherIndicator = !isPricePanel && !isFirstLayerInPanel && !isOverlayOfIndicator;

        if (isPriceDataset) {
          for (const styleNode of styleNodes) {
            if (!doesNodeHaveAttribute(styleNode, 'type', false)) {
              throw new Error(`${ERROR_MESSAGE} - price panel dataset style node does not have a type attribute.`);
            }

            const styleNodeType = getAttributeValue(styleNode, 'type');
            const styleNodeArgs = getAttributeValue(styleNode, 'args');

            const chartTypeOption = CHART_TYPE_OPTIONS_ARRAY.find(chartTypeOption =>
              chartTypeOption.sharpCharts2TypeValue.toLowerCase() === styleNodeType &&
              (chartTypeOption.sharpCharts2IsCandleBased || chartTypeOption.sharpCharts2ArgsValue.toLowerCase() === styleNodeArgs)
            );

            if (!chartTypeOption) {
              throw new Error(`${ERROR_MESSAGE} - price panel dataset style node type attribute value: ${styleNodeType} and args attribute value: ${styleNodeArgs} do not match any chart type options.`);
            }

            if (chartTypeOption.sharpCharts2IsCandleBased && styleNodeArgs === 'solid') {
              solidCandles = true;
            }

            chartType = chartTypeOption.value;

            const styleNodeAlpha = getAttributeValue(styleNode, 'alpha');
            const opacityOption = OPACITY_OPTIONS_ARRAY.find(opacityOption =>
              opacityOption.sharpCharts2AlphaValue === styleNodeAlpha ||
              (isValidNumber(opacityOption.sharpCharts2AlphaValue) && isValidNumber(styleNodeAlpha) && Number(opacityOption.sharpCharts2AlphaValue) === Number(styleNodeAlpha))
            );

            if (!opacityOption) {
              throw new Error(`${ERROR_MESSAGE} - price panel dataset style node alpha attribute value: ${styleNodeAlpha} does not match any opacity options.`);
            }

            opacity = opacityOption.title;

            if (!doesNodeHaveAttribute(styleNode, 'signal', false)) {
              const styleNodeSwatchNumber = getAttributeValue(styleNode, 'swatchnumber');
              const isValidStyleNodeSwatchNumber = isValidNumber(styleNodeSwatchNumber, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true }) && Number(styleNodeSwatchNumber) < colorScheme.sharpCharts2OverlaySwatches.length;

              if (isValidStyleNodeSwatchNumber) {
                upColor = colorScheme.sharpCharts2OverlaySwatches[Number(styleNodeSwatchNumber)].upDownColorOption;
                downColor = colorScheme.sharpCharts2OverlaySwatches[Number(styleNodeSwatchNumber)].upDownColorOption;
              } else {
                upColor = UP_DOWN_COLOR_OPTION_AUTO;
                downColor = UP_DOWN_COLOR_OPTION_AUTO;
                const upColorRgb = colorScheme.defaultSharpCharts2OverlayLineSwatchUpColor;
                const downColorOption = colorScheme.sharpCharts2OverlaySwatches.find(sharpCharts2OverlaySwatch =>
                  sharpCharts2OverlaySwatch.lineColor.toLowerCase() === upColorRgb.toLowerCase()
                );

                if (downColorOption) {
                  downColor = downColorOption.upDownColorOption;
                }
              }
            }
          }

          continue;
        }

        if (isOverlay) {
          const overlayOption = STANDARD_OVERLAYS_ARRAY.find(standardOverlayOption => doesNodeHaveAttributePair(datasetNode, 'class', standardOverlayOption.sharpCharts2Id));

          if (!overlayOption) {
            continue;
          }

          const overlay = getBlankOverlay();

          overlay.overlayName = overlayOption.name;
          let parameters = getAttributeValue(datasetNode, 'args', false);

          if (overlayOption === PRICE_SAME_SCALE) {
            parameters = parameters.replace(new RegExp('data\\.symbol', 'ig'), '$SYMBOL');
          }

          overlay.parameters = formatIndicatorAndOverlayParameters(parameters);

          let overlayStyleType = '';
          let overlayStyleArgs = '';
          let overlayStyleSwatchNumber = '';
          let isValidOverlayStyleNodeSwatchNumber = false;
          let overlayStyleAlpha = '';
          const firstStyleNode = styleNodes[0];

          if (firstStyleNode) {
            overlayStyleType = doesNodeHaveAttribute(firstStyleNode, 'type', false) ? getAttributeValue(firstStyleNode, 'type', false) : overlayStyleType;
            overlayStyleArgs = doesNodeHaveAttribute(firstStyleNode, 'args', false) ? getAttributeValue(firstStyleNode, 'args', false) : overlayStyleArgs;
            overlayStyleSwatchNumber = doesNodeHaveAttribute(firstStyleNode, 'swatchnumber', false) ? getAttributeValue(firstStyleNode, 'swatchnumber', false) : overlayStyleSwatchNumber;
            isValidOverlayStyleNodeSwatchNumber = isValidNumber(overlayStyleSwatchNumber, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true }) && Number(overlayStyleSwatchNumber) < colorScheme.sharpCharts2OverlaySwatches.length;
            overlayStyleAlpha = doesNodeHaveAttribute(firstStyleNode, 'alpha', false) ? getAttributeValue(firstStyleNode, 'alpha', false) : overlayStyleAlpha;
          }

          let overlayStyle = OVERLAY_STYLES_ARRAY.find(option =>
            option.sharpCharts2TypeId.toLowerCase() === overlayStyleType.trim().toLowerCase() &&
            option.sharpCharts2Args.toLowerCase() === overlayStyleArgs.trim().toLowerCase()
          );
          let overlayOpacity = OPACITY_OPTIONS_ARRAY.find(option =>
            option.sharpCharts2AlphaValue.toLowerCase() === overlayStyleAlpha.trim().toLowerCase()
          );

          if (overlayOption === VOLUME_BY_PRICE) {
            overlayStyle = undefined;

            if (overlayOpacity === OPACITY_OPTION_AUTO) {
              overlayOpacity = OPACITY_OPTION_ZERO_POINT_FIVE;
            }
          }

          if (overlayOption === ICHIMOKU_CLOUD || overlayOption === ICHIMOKU_CLOUD_FULL) {
            overlayStyle = OVERLAY_STYLE_AREA;
          }

          if (overlayStyle) {
            overlay.overlayStyle = overlayStyle.title;
          }

          if (isValidOverlayStyleNodeSwatchNumber && overlayOption !== VOLUME_BY_PRICE) {
            overlay.overlayColor = colorScheme.sharpCharts2OverlaySwatches[Number(overlayStyleSwatchNumber)].upDownColorOption;
          }

          if (overlayOpacity) {
            overlay.overlayOpacity = overlayOpacity.title;
          }

          sortedOverlays.push(overlay);
          continue;
        }

        if (!isIndicatorPositionedBehindPrice && !isIndicatorPositionedAboveOrBelowPrice && !isIndicatorPositionedBehindAnotherIndicator) {
          continue;
        }

        const indicatorClass = getAttributeValue(datasetNode, 'class');
        const indicatorOption = STANDARD_INDICATORS_ARRAY.find(standardIndicatorOption =>
          standardIndicatorOption.sharpCharts2Id.toLowerCase() === indicatorClass
        );

        if (!indicatorOption) {
          continue;
        }

        const indicatorName = indicatorOption.name;
        let indicatorPosition;

        if (isIndicatorPositionedBehindPrice) {
          indicatorPosition = INDICATOR_POSITION_BEHIND_PRICE.title;
        } else if (isIndicatorPositionedBehindAnotherIndicator) {
          indicatorPosition = INDICATOR_POSITION_BEHIND_INDICATOR.title;
        } else if (!hasParsedPricePanel) {
          indicatorPosition = INDICATOR_POSITION_ABOVE.title;
        } else {
          indicatorPosition = INDICATOR_POSITION_BELOW.title;
        }

        let parameters = getAttributeValue(datasetNode, 'args', false);

        if (
          indicatorOption === DATE_TIME_AXIS &&
          (!parameters || parameters.toLowerCase() !== 'user-defined')
        ) {
          continue;
        }

        if (indicatorOption === DATE_TIME_AXIS) {
          parameters = '';
        } else if (indicatorOption === VOLUME) {
          if (parameters === 'user-defined') {
            parameters = '';
          }
        } else if (indicatorOption === PRICE || indicatorOption === PRICE_PERFORMANCE) {
          parameters = parameters.replace(new RegExp('data\\.symbol', 'ig'), '$SYMBOL');

          if (scaleNode && doesNodeHaveAttributePair(scaleNode, 'log', 'true')) {
            parameters = `${parameters},log`;
          }

          if (scaleNode && doesNodeHaveAttributePair(scaleNode, 'inverted', 'true')) {
            parameters = `${parameters},inverted`;
          }
        }

        let indicatorPriceStyle = DEFAULT_PRICE_INDICATOR_STYLE_OPTION.title;
        let indicatorColor = UP_DOWN_COLOR_OPTION_AUTO;
        const firstStyleNode = styleNodes[0];

        if (firstStyleNode && indicatorOption !== DATE_TIME_AXIS) {
          if (indicatorOption === PRICE || indicatorOption === PRICE_PERFORMANCE) {
            const styleType = getAttributeValue(firstStyleNode, 'type');
            const styleArgs = getAttributeValue(firstStyleNode, 'args');

            const priceStyle = PRICE_INDICATOR_STYLE_OPTIONS_ARRAY.find(priceIndicatorStyleOption =>
              priceIndicatorStyleOption.sharpCharts2StyleType.toLowerCase() === styleType &&
              priceIndicatorStyleOption.sharpCharts2StyleArgs.toLowerCase() === styleArgs
            );

            if (priceStyle) {
              indicatorPriceStyle = priceStyle.title;
            }
          }

          const styleSwatchNumber = getAttributeValue(firstStyleNode, 'swatchnumber');
          const isValidStyleSwatchNumber = isValidNumber(styleSwatchNumber, { isRequiredToBeInteger: true, isRequiredToBeGreaterThanOrEqualToZero: true }) && Number(styleSwatchNumber) < colorScheme.sharpCharts2OverlaySwatches.length;

          if (isValidStyleSwatchNumber) {
            indicatorColor = colorScheme.sharpCharts2OverlaySwatches[Number(styleSwatchNumber)].upDownColorOption;
          }
        }

        let heightValue = '';

        if (isIndicatorPositionedAboveOrBelowPrice) {
          heightValue = getAttributeValue(panelNode, 'height');
        } else {
          heightValue = scaleNode ? getAttributeValue(scaleNode, 'height') : '';
        }

        const heightOption = INDICATOR_HEIGHT_OPTIONS_ARRAY.find(indicatorHeightOption =>
          indicatorHeightOption.sharpCharts2Id.toLowerCase() === heightValue ||
          (isValidNumber(indicatorHeightOption.sharpCharts2Id) && isValidNumber(heightValue) && Number(indicatorHeightOption.sharpCharts2Id) === Number(heightValue))
        );
        const indicatorHeight = heightOption && indicatorOption !== DATE_TIME_AXIS ? heightOption.title : DEFAULT_INDICATOR_HEIGHT_OPTION.title;

        const opacityValue = getAttributeValue(layerNode, 'alpha');
        const opacityOption = OPACITY_OPTIONS_ARRAY.find(opacityOptionItem =>
          opacityOptionItem.sharpCharts2AlphaValue.toLowerCase() === opacityValue ||
          (isValidNumber(opacityOptionItem.sharpCharts2AlphaValue) && isValidNumber(opacityValue) && Number(opacityOptionItem.sharpCharts2AlphaValue) === Number(opacityValue))
        );
        const indicatorOpacity = opacityOption && indicatorOption !== DATE_TIME_AXIS ? opacityOption.title : OPACITY_OPTION_AUTO.title;
        const indicatorId = getAttributeValue(datasetNode, 'id');
        let indicatorOverlayDataset;

        if (indicatorId && indicatorOption !== DATE_TIME_AXIS) {
          for (const p of panelNodes) {
            for (const l of getChildNodes(p, 'layer')) {
              for (const d of getChildNodes(l, 'dataset')) {
                const datasetParent = getAttributeValue(d, 'parent');

                if (datasetParent === indicatorId) {
                  indicatorOverlayDataset = d;
                }
              }
            }
          }
        }

        const indicatorOverlayClass = indicatorOverlayDataset ? getAttributeValue(indicatorOverlayDataset, 'class') : '';
        const indicatorOverlayOption = OVERLAYS_OF_INDICATORS_ARRAY.find(overlayOfIndicatorOption =>
          overlayOfIndicatorOption.sharpCharts2Id.toLowerCase() === indicatorOverlayClass
        );
        const overlayName = indicatorOverlayOption ? indicatorOverlayOption.name : NONE;
        const overlayParameters = indicatorOverlayOption && indicatorOverlayDataset ? getAttributeValue(indicatorOverlayDataset, 'args', false) : '';
        let formattedParameters = formatIndicatorAndOverlayParameters(parameters);
        const formattedOverlayParameters = formatIndicatorAndOverlayParameters(overlayParameters);

        if (indicatorOption === PRICE || indicatorOption === PRICE_PERFORMANCE) {
          formattedParameters = formatPriceIndicatorParameters(formattedParameters);
        }

        sortedIndicators.push({
          ...getBlankIndicator(),
          indicatorName,
          parameters: formattedParameters,
          indicatorPosition,
          indicatorPriceStyle,
          indicatorColor,
          indicatorHeight,
          indicatorOpacity,
          overlayName,
          overlayParameters: formattedOverlayParameters
        });
      }

      if (isHeaderPanel || isFullQuotePanel) {
        break;
      }

      if (isPriceLayer) {
        isLogScale = !!scaleNode && doesNodeHaveAttributePair(scaleNode, 'log', 'true');
      }
    }
  }

  const dataCompareSymbolsPropertyNode = getPropertyNodeOrNull(propertyNodes, 'data.compare.symbols', true);

  if (dataCompareSymbolsPropertyNode && doesNodeHaveAttribute(dataCompareSymbolsPropertyNode, 'value', true)) {
    chartType = CHART_TYPE_OPTION_PERF_CHART.value;
  }

  return {
    chartType,
    opacity,
    upColor,
    downColor,
    fullQuote,
    priceLabels,
    isLogScale,
    solidCandles,
    chartSettingsVolumeMenuValue,
    colorVolume,
    sortedIndicators: sortedIndicators.slice(0, MAXIMUM_NUMBER_OF_INDICATORS),
    sortedOverlays: sortedOverlays.slice(0, MAXIMUM_NUMBER_OF_OVERLAYS)
  };
}

export function parseSharpCharts2XML(xmlString: string): ChartStyle {
  const xmlDocument = new DOMParser().parseFromString(xmlString, 'text/xml');

  if (xmlDocument.children.length !== 1) {
    throw new Error(`${ERROR_MESSAGE} - xmlDocument.children.length is not 1: ${xmlDocument.children.length}.`);
  }

  if (xmlDocument.children[0].nodeName.trim().toLowerCase() !== 'sharpchart') {
    throw new Error(`${ERROR_MESSAGE} - xmlDocument.children[0].nodeName is not "sharpchart": ${xmlDocument.children[0].nodeName}.`);
  }

  const sharpChartNode = xmlDocument.children[0];
  const masterDatasetNode = getChildNodes(sharpChartNode, 'dataset').find(child =>
    doesNodeHaveAttributePair(child, 'id', 'master') &&
    doesNodeHaveAttributePair(child, 'symbol', 'data.symbol')
  );

  if (!masterDatasetNode) {
    throw new Error(`${ERROR_MESSAGE} - no master dataset node found.`);
  }

  const panelNodes = getChildNodes(sharpChartNode, 'panel');
  let propertyNodes = getChildNodes(sharpChartNode, 'property').filter(child => doesNodeHaveAttribute(child, 'name', false));

  const gridShowLayerTitlesIndexes = [];
  const gridShowOverlayTitlesIndexes = [];

  for (let i = 0; i < propertyNodes.length; i++) {
    if (doesNodeHaveAttributePair(propertyNodes[i], 'name', 'grid.show.layerTitles')) {
      gridShowLayerTitlesIndexes.push(i);
      continue;
    }

    if (doesNodeHaveAttributePair(propertyNodes[i], 'name', 'grid.show.overlayTitles')) {
      gridShowOverlayTitlesIndexes.push(i);
    }
  }

  const gridShowLayerTitlesIndexesToRemove = gridShowLayerTitlesIndexes.length > 1 ? gridShowLayerTitlesIndexes.slice(0, gridShowLayerTitlesIndexes.length - 1) : [];
  const gridShowOverlayTitlesIndexesToRemove = gridShowOverlayTitlesIndexes.length > 1 ? gridShowOverlayTitlesIndexes.slice(0, gridShowOverlayTitlesIndexes.length - 1) : [];
  const filteredPropertyNodes = [];

  for (let i = 0; i < propertyNodes.length; i++) {
    if (gridShowLayerTitlesIndexesToRemove.includes(i) || gridShowOverlayTitlesIndexesToRemove.includes(i)) {
      continue;
    }

    filteredPropertyNodes.push(propertyNodes[i]);
  }

  propertyNodes = filteredPropertyNodes;

  const colorScheme = parseColorScheme(propertyNodes);
  const chartSize = parseChartSize(propertyNodes);

  const chartStoreSettings: ChartStyle = {
    colorScheme: colorScheme.value,
    chartSize: chartSize.title,
    ...getCustomChartWidthAndCustomChartHeight(chartSize, propertyNodes),
    ...getCustomColorSchemeColors(colorScheme, propertyNodes),
    ...getChartGrid(propertyNodes),
    ...getPeriod(propertyNodes),
    ...getRange(propertyNodes),
    ...getExtraBars(propertyNodes),
    ...getLegends(propertyNodes),
    ...getYAxisLabels(propertyNodes),
    ...getZoomThumbnail(propertyNodes),
    ...getExtendedHours(propertyNodes),
    ...getUseMainChartSymbolAsBaseline(propertyNodes),
    ...getPerfChartSymbolsObjects(propertyNodes),
    ...getKagiRelatedProperties(masterDatasetNode),
    ...getRenkoRelatedProperties(masterDatasetNode),
    ...getChartStoreSettingsFromPanelNodes(colorScheme, propertyNodes, panelNodes)
  };

  window.sc3WorkbenchLogger.info('chartStoreSettings', chartStoreSettings);

  return chartStoreSettings;
}
